<template>
  <section class="creative-workshop">
    <div class="section-header">
      <div class="section-heading">
        <span class="section-icon"><v-icon>mdi-lightbulb-on-outline</v-icon></span>
        <div>
          <div class="section-eyebrow">{{ pageEyebrow }}</div>
          <h2>{{ pageTitle }}</h2>
          <p>{{ pageDescription }}</p>
        </div>
      </div>
    </div>

    <div v-if="loading" class="category-grid" aria-label="Kategorien werden geladen">
      <v-skeleton-loader v-for="index in 4" :key="index" dark type="image, article" class="category-skeleton" />
    </div>

    <div v-else-if="categories.length" class="category-grid">
      <v-card
        v-for="category in categories"
        :key="category._id"
        dark
        flat
        class="category-card"
        role="button"
        tabindex="0"
        @click="openCategory(category)"
        @keydown.enter="openCategory(category)"
        @keydown.space.prevent="openCategory(category)">
        <div class="category-top">
          <span
            class="category-icon"
            :style="{
              '--tier-color': tierStyle(category.tier).color,
              '--tier-background': tierStyle(category.tier).background,
            }">
            <v-icon>{{ category.icon || "mdi-lightbulb-on-outline" }}</v-icon>
          </span>
          <span
            class="tier-pill"
            :style="{
              '--tier-color': tierStyle(category.tier).color,
              '--tier-background': tierStyle(category.tier).background,
            }">
            {{ tierLabel(category.tier) }}
          </span>
        </div>

        <div class="category-copy">
          <h3>{{ category.title }}</h3>
          <p>{{ category.description || "Praxiswissen, Ideen und Erfahrungen aus diesem Themenbereich." }}</p>
        </div>

        <div class="category-footer">
          <span><v-icon>mdi-message-text-outline</v-icon><strong>{{ category.ideaCount || 0 }}</strong> Beiträge</span>
          <v-icon class="category-arrow">mdi-arrow-right</v-icon>
        </div>
      </v-card>
    </div>

    <div v-else class="empty-surface">
      <span class="empty-icon"><v-icon>mdi-lightbulb-off-outline</v-icon></span>
      <h3>Noch keine Themen freigeschaltet</h3>
      <p>{{ emptyDescription }}</p>
      <v-btn outlined color="#58d388" class="empty-action" :to="emptyActionPath">
        <v-icon left>mdi-book-open-page-variant-outline</v-icon>
        {{ emptyActionLabel }}
      </v-btn>
    </div>
  </section>
</template>

<script>
import api from "@/api"

export default {
  name: "Kreativwerkstatt",
  props: {
    track: {
      type: String,
      default: "license",
      validator: (value) => ["license", "leadership"].includes(value),
    },
  },
  data() {
    return {
      categories: [],
      loading: true,
    }
  },
  computed: {
    isLeadership() {
      return this.track === "leadership"
    },
    pageEyebrow() {
      return this.isLeadership ? "Ideen für Führung" : "Aus der Praxis"
    },
    pageTitle() {
      return this.isLeadership ? "Leadership Creative" : "Kreativwerkstatt"
    },
    pageDescription() {
      return this.isLeadership
        ? "Entdecke und teile kreative Führungsimpulse in einem geschützten Raum. Themen werden nach Abschluss des jeweiligen Leadership-Moduls freigeschaltet."
        : "Tausche erprobte Ideen mit anderen aus. Freigeschaltet werden Themen aus Modulen, die du bereits abgeschlossen hast."
    },
    emptyDescription() {
      return this.isLeadership
        ? "Schliesse zuerst das entsprechende Leadership-Modul ab. Danach kannst du hier Ideen entdecken und teilen."
        : "Schliesse zuerst ein Modul ab. Danach kannst du hier Best Practices entdecken und teilen."
    },
    emptyActionPath() {
      return this.isLeadership ? "/akademie/leadership/coaching" : "/akademie/suite"
    },
    emptyActionLabel() {
      return this.isLeadership ? "Zum Leadership Coaching" : "Zur Learning Suite"
    },
  },
  watch: {
    track() {
      this.loadCategories()
    },
  },
  mounted() {
    this.loadCategories()
  },
  methods: {
    async loadCategories() {
      this.loading = true
      this.categories = []
      try {
        const moduleRequest = this.isLeadership ? api.akademie.fetchLeadershipModules() : api.akademie.fetchModules()
        const progressRequest = this.isLeadership
          ? api.akademie.fetchProgress({ track: "leadership" })
          : api.akademie.fetchProgress()
        const [modules, progressEntries] = await Promise.all([moduleRequest, progressRequest])
        if (modules && progressEntries) {
          this.categories = modules.filter((moduleItem) => {
            const progress = progressEntries.find(
              (entry) =>
                entry.module === moduleItem._id ||
                entry.module === moduleItem.id ||
                (entry.module && (entry.module._id === moduleItem._id || entry.module.id === moduleItem.id)),
            )
            return progress && progress.completedAt
          })
        }
      } catch (error) {
        console.error("Fehler beim Laden der Kategorien:", error)
      } finally {
        this.loading = false
      }
    },
    openCategory(category) {
      const basePath = this.isLeadership ? "/akademie/leadership/creative" : "/akademie/kreativwerkstatt"
      this.$router.push(`${basePath}/${category._id}`)
    },
    tierLabel(tier) {
      if (this.isLeadership) return "Leadership"
      const labels = { bronze: "Bronze", silber: "Silber", gold: "Gold" }
      return labels[tier] || "Akademie"
    },
    tierStyle(tier) {
      const styles = {
        bronze: { color: "#d89557", background: "rgba(205, 127, 50, 0.14)" },
        silber: { color: "#d4d4d8", background: "rgba(192, 192, 192, 0.12)" },
        gold: { color: "#f4c95d", background: "rgba(255, 215, 0, 0.12)" },
      }
      return styles[tier] || { color: "#58d388", background: "rgba(0, 150, 64, 0.12)" }
    },
  },
}
</script>

<style scoped>
.creative-workshop {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  min-height: 30rem;
  padding: 0.375rem 0 2rem;
  font-size: 1rem;
}

.section-header {
  margin-bottom: 1rem;
  padding: 1.25rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.075), transparent 52%), rgba(255, 255, 255, 0.03);
}

.section-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}

.section-heading > div {
  min-width: 0;
}

.section-icon,
.empty-icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.section-icon {
  width: 2.875rem;
  height: 2.875rem;
  border: 1px solid rgba(0, 150, 64, 0.24);
  border-radius: 0.875rem;
}

.section-icon .v-icon,
.empty-icon .v-icon,
.category-icon .v-icon {
  color: currentColor;
}

.section-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.section-heading h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.5rem;
  line-height: 1.25;
}

.section-heading p {
  max-width: 49rem;
  margin: 0.375rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.55;
}

.category-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.875rem;
}

.category-skeleton,
.category-card {
  min-height: 15.5rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem !important;
  background: var(--surface) !important;
}

.category-card {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  outline: none;
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.category-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.category-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.category-icon {
  display: grid;
  width: 3rem;
  height: 3rem;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  color: var(--tier-color);
  background: var(--tier-background);
}

.tier-pill {
  min-height: 1.75rem;
  padding: 0.3125rem 0.625rem;
  border-radius: 999px;
  color: var(--tier-color);
  background: var(--tier-background);
  font-size: 0.75rem;
  font-weight: 800;
}

.category-copy {
  flex: 1 1 auto;
  margin-top: 1rem;
}

.category-copy h3 {
  margin: 0;
  color: var(--text);
  font-size: 1rem;
  line-height: 1.35;
}

.category-copy p {
  display: -webkit-box;
  margin: 0.5rem 0 0;
  overflow: hidden;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.category-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 0.875rem;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
}

.category-footer span {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: #c7c7cb;
  font-size: 0.75rem;
}

.category-footer span .v-icon {
  color: #58d388;
  font-size: 1.125rem;
}

.category-footer strong {
  color: #ffffff;
  font-size: 0.875rem;
}

.category-arrow {
  color: #8f9095 !important;
}

.empty-surface {
  display: flex;
  min-height: 20rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 2rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem;
  background: var(--surface);
  text-align: center;
}

.empty-icon {
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.875rem;
  border-radius: 1rem;
}

.empty-surface h3 {
  margin: 0;
  color: var(--text);
  font-size: 1rem;
}

.empty-surface p {
  max-width: 30rem;
  margin: 0.375rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.55;
}

.empty-action {
  min-height: 2.75rem !important;
  margin-top: 1rem;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

@media (hover: hover) and (pointer: fine) {
  .category-card:hover {
    transform: translateY(-2px);
    border-color: var(--border-strong);
    background: var(--surface-strong) !important;
  }
}

@media (max-width: 78.9375rem) {
  .category-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .creative-workshop {
    padding-bottom: 1rem;
  }

  .section-header {
    padding: 1rem;
    border-radius: 1.0625rem;
  }

  .section-heading {
    gap: 0.75rem;
  }

  .section-icon {
    width: 2.625rem;
    height: 2.625rem;
  }

  .section-heading h2 {
    font-size: 1.25rem;
  }

  .category-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .category-card {
    min-height: 14rem;
  }
}
</style>
